'use client'

import * as React from 'react'

interface ScrollContextValue {
  scrollRef: React.RefObject<HTMLDivElement>
  scrollTo: (options: ScrollToOptions) => void
  scrollToTop: (options?: { behavior?: ScrollBehavior }) => void
  scrollToBottom: (options?: { behavior?: ScrollBehavior }) => void
  scrollIntoView: (
    element: HTMLElement,
    options?: { behavior?: ScrollBehavior },
  ) => void
  isScrolledToTop: boolean
  isScrolledToBottom: boolean
  scrollHeight: number
  scrollTop: number
  clientHeight: number
  onScroll?: (event: Event) => void
}

const ScrollContext = React.createContext<ScrollContextValue | undefined>(
  undefined,
)

export function ScrollProvider({
  children,
  scrollRef,
  onScroll,
}: {
  children: React.ReactNode
  scrollRef: React.RefObject<HTMLDivElement>
  onScroll?: (event: Event) => void
}) {
  const [isScrolledToTop, setIsScrolledToTop] = React.useState(true)
  const [isScrolledToBottom, setIsScrolledToBottom] = React.useState(false)
  const [scrollHeight, setScrollHeight] = React.useState(0)
  const [scrollTop, setScrollTop] = React.useState(0)
  const [clientHeight, setClientHeight] = React.useState(0)

  const framePendingRef = React.useRef(false)
  const rafIdRef = React.useRef<number | null>(null)

  const handleScroll = React.useCallback(
    (event: Event) => {
      if (!scrollRef.current) return
      if (framePendingRef.current) return
      framePendingRef.current = true
      rafIdRef.current = requestAnimationFrame(() => {
        framePendingRef.current = false
        const { scrollTop, scrollHeight, clientHeight } = scrollRef.current!
        setScrollTop(scrollTop)
        setScrollHeight(scrollHeight)
        setClientHeight(clientHeight)
        setIsScrolledToTop(scrollTop === 0)
        setIsScrolledToBottom(
          Math.abs(scrollHeight - clientHeight - scrollTop) < 1,
        )

        // Call the onScroll callback if provided
        onScroll?.(event)
      })
    },
    [scrollRef, onScroll],
  )

  React.useEffect(() => {
    const scrollElement = scrollRef.current
    if (!scrollElement) return

    scrollElement.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll(new Event('scroll')) // Initial check

    return () => {
      scrollElement.removeEventListener('scroll', handleScroll)
      if (rafIdRef.current != null) cancelAnimationFrame(rafIdRef.current)
      framePendingRef.current = false
    }
  }, [scrollRef, handleScroll])

  const scrollTo = React.useCallback(
    (options: ScrollToOptions) => {
      scrollRef.current?.scrollTo(options)
    },
    [scrollRef],
  )

  const scrollToTop = React.useCallback(
    (options?: { behavior?: ScrollBehavior }) => {
      scrollTo({ top: 0, behavior: options?.behavior || 'smooth' })
    },
    [scrollTo],
  )

  const scrollToBottom = React.useCallback(
    (options?: { behavior?: ScrollBehavior }) => {
      if (!scrollRef.current) return
      scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: options?.behavior || 'smooth',
      })
    },
    [scrollTo],
  )

  const scrollIntoView = React.useCallback(
    (element: HTMLElement, options?: { behavior?: ScrollBehavior }) => {
      element.scrollIntoView({
        behavior: options?.behavior || 'smooth',
        block: 'nearest',
      })
    },
    [],
  )

  const value = React.useMemo(
    () => ({
      scrollRef,
      scrollTo,
      scrollToTop,
      scrollToBottom,
      scrollIntoView,
      isScrolledToTop,
      isScrolledToBottom,
      scrollHeight,
      scrollTop,
      clientHeight,
      onScroll,
    }),
    [
      scrollRef,
      scrollTo,
      scrollToTop,
      scrollToBottom,
      scrollIntoView,
      isScrolledToTop,
      isScrolledToBottom,
      scrollHeight,
      scrollTop,
      clientHeight,
      onScroll,
    ],
  )

  return (
    <ScrollContext.Provider value={value}>{children}</ScrollContext.Provider>
  )
}

export function useScroll() {
  const context = React.useContext(ScrollContext)
  if (!context) {
    throw new Error('useScroll must be used within a ScrollProvider')
  }
  return context
}
